import { useState } from "react";
import { Flag } from "@phosphor-icons/react";
import api from "../lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";

const REASONS = [
  "Harmful or self-harm content",
  "Harassment or bullying",
  "Spam or advertising",
  "Hate speech",
  "Other",
];

export const ReportDialog = ({ postId, open, onOpenChange }) => {
  const [reason, setReason] = useState(REASONS[0]);
  const [details, setDetails] = useState("");
  const [sending, setSending] = useState(false);

  const submit = async () => {
    setSending(true);
    try {
      const full = details.trim() ? `${reason} — ${details.trim()}` : reason;
      const res = await api.post(`/posts/${postId}/report`, { reason: full });
      toast.success(res.data.message || "Thanks for reporting");
      onOpenChange(false);
      setDetails("");
    } catch {
      toast.error("Could not submit your report");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-[2rem] border-[#E6EBE4] max-w-md" data-testid="report-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl text-[#2C362B] flex items-center gap-2">
            <Flag size={22} weight="duotone" color="#D97B6A" /> Report this post
          </DialogTitle>
          <DialogDescription className="text-[#6D7A6A]">
            Help keep this space safe. Reports are private and reviewed by our team.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 py-2">
          {REASONS.map((r) => (
            <button
              key={r}
              onClick={() => setReason(r)}
              data-testid={`report-reason-${r.split(" ")[0].toLowerCase()}`}
              className={`w-full text-left rounded-2xl px-4 py-3 text-sm font-semibold transition-all border ${
                reason === r ? "bg-[#F4F1E8] border-[#8A9A86] text-[#2C362B]" : "bg-white border-[#E6EBE4] text-[#515A4F] hover:border-[#CCD5C9]"
              }`}
            >
              {r}
            </button>
          ))}
          <textarea
            value={details}
            onChange={(e) => setDetails(e.target.value)}
            placeholder="Add any details (optional)…"
            maxLength={400}
            className="w-full bg-[#F4F1E8] border-none rounded-2xl px-4 py-3 text-[#2C362B] placeholder:text-[#A8B0A3] focus:ring-2 focus:ring-[#8A9A86] focus:bg-white resize-none outline-none transition-all min-h-[70px] mt-2"
            data-testid="report-details-input"
          />
        </div>

        <DialogFooter>
          <button onClick={() => onOpenChange(false)} className="rounded-full px-6 py-2.5 font-semibold text-[#6D7A6A] hover:bg-[#F4F1E8] transition-colors" data-testid="report-cancel-btn">
            Cancel
          </button>
          <button onClick={submit} disabled={sending} className="bg-[#D97B6A] text-white hover:bg-[#c96a59] rounded-full px-6 py-2.5 font-semibold transition-all disabled:opacity-60" data-testid="report-submit-btn">
            {sending ? "Submitting…" : "Submit report"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
